import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type SortingState } from "@tanstack/react-table";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import type {
  ColDef,
  ICellRendererParams,
  CellClickedEvent
} from "ag-grid-community";
import { Checkbox, CheckboxSize } from "shared/components";
import {
  AgGrid,
  DEFAULT_AG_GRID_VIEWPORT_OFFSET
} from "shared/components/AgGrid";
import {
  Lead,
  LeadType,
  NON_PUNITIVE_FRAUD_INDICATOR_TYPES
} from "views/leads-id/types";
import { cx } from "shared/utils/classnames";
import { LeadTypeBadge } from "shared/app/LeadsTableView/components/LeadTypeBadge";
import { ApprovalLikelihoodBadge } from "shared/app/LeadsTableView/components/ApprovalLikelihoodBadge";
import { WaitlistLeadIndicator } from "shared/app/LeadsTableView/components/WaitlistLeadIndicator";
import { useLayout } from "providers/Layout/hooks";
import { formatDate } from "views/applications-id-report/utils";
import { LastNoteCell } from "shared/app/Notes/components/LastNoteCell";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { formatPhone } from "shared/utils";
import { LEAD_ACTIVITY_TYPE_LABELS } from "shared/app/LeadsTableView/slices/LeadsTable/constants";
import { SelectPopUp } from "shared/app/LeadsTableView/slices/LeadsTable/components/SelectPopUp";
import { Stage } from "views/settings-crm/types/stage";
import { StageSelect } from "shared/app/LeadsTableView/slices/LeadsTable/components/StageSelect";
import { AssigneeSelect } from "shared/app/LeadsTableView/slices/LeadsTable/components/AssigneeSelect";
import { StarCell } from "shared/app/LeadsTableView/slices/LeadsTable/components/StarCell";
import { MessageCell } from "shared/app/LeadsTableView/slices/LeadsTable/components/MessageCell";
import { HeaderWithSort } from "shared/app/TaskTableView/slices/TaskTable/HeaderWithSort";
import { formatTaskCreatedDate } from "shared/app/TaskTableView/constants";
import { ColumnVisibilityItem } from "providers/Auth";
import {
  LEADS_COLUMN_ORDER,
  LeadColumnId
} from "shared/app/LeadsTableView/constants";

const CHECKBOX_COL_ID = "checkbox";
const STAR_COL_ID = "star";
const MESSAGE_COL_ID = "message";
// Stable empty default so an omitted ownerStagesData doesn't hand a fresh [] to
// the ownerStageOptions memo every render.
const EMPTY_STAGES: Stage[] = [];

export const LeadsTable = ({
  data,
  stagesData,
  ownerStagesData = EMPTY_STAGES,
  isLoading,
  pagination,
  perPageRows,
  viewPortHeightOffset,
  sorting,
  onSortingChange,
  columnsVisibility,
  isOwnerLeadsEnabled = false,
  isLeadScoringEnabled = false,
  hidePropertyInterested = false
}: {
  data?: Lead[];
  stagesData: Stage[];
  ownerStagesData?: Stage[];
  isLoading?: boolean;
  pagination?: React.ReactNode;
  perPageRows?: number;
  viewPortHeightOffset?: number;
  sorting: SortingState;
  onSortingChange: (next: SortingState) => void;
  columnsVisibility: ColumnVisibilityItem[];
  isOwnerLeadsEnabled?: boolean;
  isLeadScoringEnabled?: boolean;
  hidePropertyInterested?: boolean;
}) => {
  const navigate = useNavigate();
  const dictionary = useUnitsPropertiesDictionary();
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const clearSelectedRows = () => {
    setSelectedRows(new Set());
  };

  const toggleRowSelection = (id: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleSort = (columnKey: string) => {
    const existingSort = sorting.find((s) => s.id === columnKey);

    let newSorting: SortingState;

    if (existingSort)
      newSorting = sorting.map((s) =>
        s.id === columnKey ? { ...s, desc: !s.desc } : s
      );
    else newSorting = [{ id: columnKey, desc: true }];

    onSortingChange(newSorting);
  };

  const getSortDirection = (columnKey: string) => {
    const sortItem = sorting.find((s) => s.id === columnKey);
    if (!sortItem) return undefined;
    return sortItem.desc ? "DESC" : ("ASC" as const);
  };

  const stageOptions = useMemo(() => {
    return stagesData.map((option) => ({
      label: String(option.name),
      value: Number(option.id),
      color: option.color
    }));
  }, [stagesData]);

  const ownerStageOptions = useMemo(() => {
    return ownerStagesData.map((option) => ({
      label: String(option.name),
      value: Number(option.id),
      color: option.color
    }));
  }, [ownerStagesData]);

  const columns = useMemo<ColDef<Lead>[]>(() => {
    const checkboxCol: ColDef<Lead> = {
      colId: CHECKBOX_COL_ID,
      headerName: "",
      width: 52,
      minWidth: 52,
      pinned: "left",
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<Lead>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <div
            className="flex items-center justify-center w-full h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              wrapperClassName="!w-auto !flex-none !p-3 justify-center"
              size={CheckboxSize.SM}
              checked={selectedRows.has(row.id)}
              onChange={() => toggleRowSelection(row.id)}
            />
          </div>
        );
      }
    };

    const starCol: ColDef<Lead> = {
      colId: STAR_COL_ID,
      headerName: "",
      width: 52,
      minWidth: 52,
      pinned: "left",
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<Lead>) => {
        const row = params.data;
        if (!row) return null;
        return <StarCell leadId={row.id} starred={row.starred} />;
      }
    };

    const messageCol: ColDef<Lead> = {
      colId: MESSAGE_COL_ID,
      headerName: "",
      width: 52,
      minWidth: 52,
      pinned: "left",
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<Lead>) => {
        const row = params.data;
        if (!row) return null;
        return <MessageCell lead={row} />;
      }
    };

    const colDefById: Record<LeadColumnId, ColDef<Lead>> = {
      [LeadColumnId.Name]: {
        colId: LeadColumnId.Name,
        headerName: "Name",
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex flex-col gap-1">
              <p className="text-content-1000 font-medium text-sm truncate">
                {row.first_name} {row.last_name}
              </p>
              <p className="text-content-400 text-xs font-normal truncate">
                {row.primary_phone
                  ? formatPhone(row.primary_phone)
                  : row.primary_email}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.PropertyInterested]: {
        colId: LeadColumnId.PropertyInterested,
        headerName: `${dictionary.Property} interested`,
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex flex-col gap-1">
              <p className="text-content-1000 font-normal text-sm truncate">
                {row.property?.name || "-"}
              </p>
              <p className="text-content-400 text-xs font-normal truncate">
                {row.property?.address.one_line_address || "-"}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.UnitInterested]: {
        colId: LeadColumnId.UnitInterested,
        headerName: `${dictionary.Unit} interested`,
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex items-center gap-1.5 min-w-0">
              {row.from_waitlist ? (
                <WaitlistLeadIndicator
                  unitTitle={row.unit?.title}
                  joinedAt={row.created_at}
                />
              ) : null}
              <p className="text-content-1000 font-normal text-sm truncate">
                {row.unit?.title || "-"}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.Type]: {
        colId: LeadColumnId.Type,
        headerName: "Type",
        minWidth: 120,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          if (!row.lead_type)
            return <span className="text-content-1000 text-sm">-</span>;
          return <LeadTypeBadge leadType={row.lead_type} />;
        }
      },
      [LeadColumnId.Source]: {
        colId: LeadColumnId.Source,
        headerName: "Source",
        minWidth: 200,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <p className="text-content-1000 font-medium text-sm truncate">
              {row.source?.name || "-"}
            </p>
          );
        }
      },
      [LeadColumnId.Score]: {
        colId: LeadColumnId.Score,
        headerName: "Approval likelihood",
        minWidth: 140,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label="Approval likelihood"
            columnKey={LeadColumnId.Score}
            sortDirection={getSortDirection(LeadColumnId.Score)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          if (
            !row.approval_likelihood_band &&
            !row.approval_likelihood_insufficient_data
          )
            return <span className="text-content-1000 text-sm">-</span>;
          return (
            <ApprovalLikelihoodBadge
              band={row.approval_likelihood_band}
              insufficientData={row.approval_likelihood_insufficient_data}
            />
          );
        }
      },
      [LeadColumnId.FraudIndicators]: {
        colId: LeadColumnId.FraudIndicators,
        headerName: "Fraud indicators",
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const count = row.fraud_indicator_count || 0;
          if (count === 0) return <p className="text-content-400 text-sm">-</p>;

          const indicators = row.fraud_indicators || [];
          const hasPunitiveIndicator =
            indicators.length === 0 ||
            indicators.some(
              (indicator) =>
                !NON_PUNITIVE_FRAUD_INDICATOR_TYPES.includes(indicator.type)
            );

          return (
            <span
              className={cx(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                hasPunitiveIndicator
                  ? "bg-danger-50 text-danger-500"
                  : "bg-warning-50 text-warning-700"
              )}
            >
              <HugeiconsIcon
                icon={AlertCircleIcon}
                size={14}
                className="shrink-0"
              />
              {count} indicator{count === 1 ? "" : "s"}
            </span>
          );
        }
      },
      [LeadColumnId.LastNote]: {
        colId: LeadColumnId.LastNote,
        headerName: "Last note",
        minWidth: 200,
        width: 300,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label="Last note"
            columnKey={LeadColumnId.LastNote}
            sortDirection={getSortDirection(LeadColumnId.LastNote)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const note = row.last_note
            ? {
                id: "",
                author: {
                  id: 0,
                  name: row.last_note.actor_name,
                  email: "",
                  author_role: "" as never
                },
                author_role: "" as never,
                message: row.last_note.rich_text?.document_text ?? "",
                rich_text: row.last_note.rich_text ?? undefined,
                created_at: row.last_note.occurred_at,
                source_name: row.last_note.source_name
              }
            : null;
          return (
            <div className="w-full" onClick={(e) => e.stopPropagation()}>
              <LastNoteCell
                note={note}
                onViewNotes={() => navigate(`/leads/${row.id}?activity=notes`)}
              />
            </div>
          );
        }
      },
      [LeadColumnId.RecentActivity]: {
        colId: LeadColumnId.RecentActivity,
        headerName: "Most recent activity",
        width: 270,
        minWidth: 220,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label="Most recent activity"
            columnKey={LeadColumnId.RecentActivity}
            sortDirection={getSortDirection(LeadColumnId.RecentActivity)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const recentActivity = row.recent_activity_summary;
          return (
            <div className="space-y-2">
              <p className="text-content-1000 font-medium text-sm truncate">
                {recentActivity
                  ? LEAD_ACTIVITY_TYPE_LABELS[recentActivity.activity_type]
                  : "-"}
              </p>
              <p className="text-content-300 text-xs font-normal">
                {recentActivity
                  ? formatDate(recentActivity.occurred_at, true)
                  : "-"}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.CreatedAt]: {
        colId: LeadColumnId.CreatedAt,
        headerName: "Created at",
        width: 180,
        minWidth: 140,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label="Created at"
            columnKey={LeadColumnId.CreatedAt}
            sortDirection={getSortDirection(LeadColumnId.CreatedAt)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const { label, time } = formatTaskCreatedDate(row.created_at);
          return (
            <div className="space-y-2">
              <p className="text-content-1000 font-medium text-sm">{label}</p>
              <p className="text-content-300 text-xs">{time}</p>
            </div>
          );
        }
      },
      [LeadColumnId.AssignedTo]: {
        colId: LeadColumnId.AssignedTo,
        headerName: "Assigned to",
        minWidth: 240,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <AssigneeSelect
              value={row.assignees?.[0]?.id ?? null}
              rowId={String(row.id)}
            />
          );
        }
      },
      [LeadColumnId.StageSelector]: {
        colId: LeadColumnId.StageSelector,
        headerName: "Current stage",
        minWidth: 240,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <StageSelect
              inputClassName="w-[210px]"
              value={row.current_stage?.id}
              options={
                row.lead_type === LeadType.Owner
                  ? ownerStageOptions
                  : stageOptions
              }
              rowId={String(row.id)}
            />
          );
        }
      }
    };

    const visibilityById = new Map(
      columnsVisibility.map((c) => [c.id, c] as const)
    );

    const orderedDynamicCols = LEADS_COLUMN_ORDER.reduce<ColDef<Lead>[]>(
      (acc, colId) => {
        // The Type column only applies to partners with the owner-leads feature;
        // for them it's a normal user-toggleable column via column visibility.
        if (colId === LeadColumnId.Type && !isOwnerLeadsEnabled) return acc;
        // The Approval likelihood column only applies to partners with the AI
        // lead-scoring feature.
        if (colId === LeadColumnId.Score && !isLeadScoringEnabled) return acc;
        if (colId === LeadColumnId.PropertyInterested && hidePropertyInterested)
          return acc;
        const visibility = visibilityById.get(colId);
        const shouldRender =
          !visibility || visibility.disabled || visibility.checked;
        if (shouldRender) acc.push(colDefById[colId]);
        return acc;
      },
      []
    );

    return [checkboxCol, starCol, messageCol, ...orderedDynamicCols];
  }, [
    selectedRows,
    stageOptions,
    ownerStageOptions,
    isOwnerLeadsEnabled,
    isLeadScoringEnabled,
    hidePropertyInterested,
    dictionary,
    sorting,
    columnsVisibility,
    navigate
  ]);

  const rows = useMemo<Lead[]>(() => data || [], [data]);

  const { sectionHeight } = useLayout();

  const selectedRowState = useMemo(() => {
    const state: Record<string, boolean> = {};
    selectedRows.forEach((id) => {
      state[String(id)] = true;
    });
    return state;
  }, [selectedRows]);

  const onCellClicked = useCallback(
    (event: CellClickedEvent<Lead>) => {
      const row = event.data;
      if (!row) return;
      const colId = event.column.getColId();
      if (
        colId === CHECKBOX_COL_ID ||
        colId === STAR_COL_ID ||
        colId === MESSAGE_COL_ID ||
        colId === "assigned_to" ||
        colId === "stage_selector"
      )
        return;
      navigate(`/leads/${row.id}`);
    },
    [navigate]
  );

  return (
    <div className="relative">
      <AgGrid<Lead>
        viewPortHeight={sectionHeight}
        viewPortHeightOffset={
          viewPortHeightOffset ?? DEFAULT_AG_GRID_VIEWPORT_OFFSET
        }
        columns={columns}
        rows={rows}
        loading={isLoading}
        onCellClicked={onCellClicked}
        footer={pagination}
        showFooter={Boolean(pagination)}
        perPageRows={perPageRows}
        rowHeight={72}
        headerHeight={44}
      />
      <SelectPopUp
        selectedRow={selectedRowState}
        clearSelectedRows={clearSelectedRows}
        stageOptions={stageOptions}
      />
    </div>
  );
};
